<template>
  <div :class="props.name" @click="handleDisabled">
    <button ref="icon" :disabled="props.isDisabled" @click="handleClick" @keyup.space="(e) => e.preventDefault()" :title="title">
      <app-icon icon="leave" />
      <span class="text" :class="[props.hideText ? 'visuallyHidden' : '']">
        <slot>{{ labelText }}</slot>
      </span>
    </button>
    <slot name="tips">
      <bubble-tip
        class="warn"
        :direction="props.tipDirection"
        :show="checkPlease"
        :buttons="[
          { event: 'cancel', text: 'Cancel' },
          { event: 'confirm', text: 'Yes' },
        ]"
        @close="close"
        @button="handleTipButton"
      >
        <template v-if="!iAmModerator">
          <strong>Leave meeting?</strong>
        </template>
        <template v-else>
          <strong>End meeting for all?</strong>
        </template>
      </bubble-tip>
    </slot>
  </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import utils from '../resources/utils.js';
import BubbleTip from '@/components/BubbleTip.vue';
import AppIcon from '@/components/AppIcon.vue';
import { localStorageKeysMap } from '../shared/constants';
import { useUiAnimations } from '@/composables/useUiAnimations.js';

import { useUsersStore } from '@/store/pinia/users';
import { usePhaseStore } from '@/store/pinia/phase';
import { useSidebarStore } from '@/store/pinia/sidebar';
import { usePageStore } from '@/store/pinia/page';

const usersStore = useUsersStore();
const phaseStore = usePhaseStore();
const sidebarStore = useSidebarStore();
const pageStore = usePageStore();
const { nopeWaggle } = useUiAnimations();

const props = defineProps({
  isDisabled: {
    required: false,
    default: false,
  },
  hideText: {
    type: Boolean,
    default: false,
  },
  tipDirection: {
    type: String,
    default: 'above',
  },
});
const emit = defineEmits(['exit', 'show-bubble', 'hide-bubble', 'nope']);
const $ee = getCurrentInstance().appContext.config.globalProperties.$ee;
const $API = getCurrentInstance().appContext.config.globalProperties.$API;

const { currentPhase } = storeToRefs(phaseStore);
const { iAmModerator, iAmInWaitingRoom } = storeToRefs(usersStore);

const checkPlease = ref(false);
const icon = ref(null);

const labelText = computed(() => (iAmModerator.value ? 'End' : 'Leave'));
const title = computed(() => (iAmModerator.value ? 'end meeting' : 'leave meeting'));
const event = computed(() => `nM:meeting:${iAmModerator.value ? 'finish' : 'leave'}`);

function close() {
  checkPlease.value = false;
  emit('hide-bubble');
}

function handleClick() {
  /**
   * intentionally avoiding `await` for `getIsEligibleForFeedback` to bootup feedback service
   * this will help us avoid cold-boot, delayed response from feedback service when its actually needed.
   * */
  $API.getIsEligibleForFeedback().catch(() => {
    console.log('cannot check if eligible for feedback');
  });
  if (currentPhase.value === 'endMeeting') {
    $ee.emit(event.value);
    emit('exit');
  } else {
    if (checkPlease.value) {
      emit('hide-bubble');
      checkPlease.value = false;
    } else {
      emit('show-bubble');
      checkPlease.value = true;
    }
  }
}

function handleTipButton(ev) {
  checkPlease.value = false;
  emit('hide-bubble');
  if (ev === 'confirm') {
    if (currentPhase.value === 'endMeeting' || !iAmModerator.value) {
      sidebarStore.setIClickedLeave(true);
    }
    nextTick(() => {
      pageStore.setUserLeftFromWaitingRoom(iAmInWaitingRoom.value);
      utils.storage.ls.removeItem(localStorageKeysMap.RELAUNCH_MEETING_ID);
      $ee.emit(event.value);
      emit('exit');
    });
  }
}

function handleDisabled() {
  if (props.isDisabled) {
    nopeWaggle(icon.value);
    emit('nope');
  }
}
</script>
<style lang="scss" scoped>
button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  .icon {
    height: rem(40px);
    width: rem(40px);
    border: 2px solid var(--c__leave-hamburger-border);
    background: var(--c__leave-hamburger-fill);
    color: var(--c__leave-hamburger-color);
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .text {
    color: var(--c__text);
    line-height: 1;
    margin-top: rem(11px);
  }

  .leave-icon :deep(svg) {
    width: rem(29px);
    height: rem(22px);
  }

  .finish-icon :deep(svg) {
    width: rem(24px);
    height: rem(24px);
  }

  @include triggered {
    .icon {
      background: var(--c__leave-hamburger-fill-hover);
      color: var(--c__leave-hamburger-color-hover);
      border-color: var(--c__leave-hamburger-border-hover);
    }
    .text {
      color: var(--c__warn-loud);
    }
  }
}
</style>
